import {
  ASHLEY_LANE_PAUSED,
  type AshleyFurnitureArmStamp,
} from '@dupe/common/affiliateRouter'
import { FeatureFlags } from '@dupe/common/constants'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import { readBootstrappedFeatureFlag } from '~/src/lib/readBootstrappedFeatureFlag'

/**
 * DUP-1920 — `toggler-ashley-furniture`. Whether this user's toggler should
 * send furniture/home traffic to **Ashley Furniture** instead of Wayfair.
 *
 * Mirrors `useAffiliateRouterArm` deliberately, including the bootstrap-cookie
 * read: on a cold client `posthog.init` runs in an effect, so `getFeatureFlag`
 * returns undefined for the first renders even though the middleware already
 * evaluated the flag and stashed it. Without the cookie the broadcast can build
 * and fire its redirect before the flag resolves, which would silently bucket
 * a treatment user into control and quietly bias the readout toward Wayfair.
 *
 * `send_event: false` on the SDK read: this is a *value* read on every render,
 * not the exposure. Exposure is captured once per surface by
 * `useAshleyFurnitureLaneExposure` below — otherwise a re-render storm would
 * bill an exposure each time and inflate the denominator.
 */
/** Every value allowed in `link_clicks.metadata.router_ashley_arm`, plus the
 *  override map treatment implies. Both moved to `@dupe/common/affiliateRouter`
 *  for step 5: the server now reconstructs the arm's overrides when it makes the
 *  routing decision itself, so the list and the map have to be shared rather than
 *  merely kept in agreement. Re-exported so existing call sites are unchanged. */
export {
  ASHLEY_FURNITURE_ARM_STAMPS,
  ASHLEY_LANE_PAUSED,
  type AshleyFurnitureArmStamp,
  ashleyFurnitureMerchantOverrides,
} from '@dupe/common/affiliateRouter'
export interface AshleyFurnitureLaneState {
  /** True only for the bucketed treatment arm. */
  enabled: boolean
  /** The resolved arm, or null while the flag is still unresolved. */
  arm: 'control' | 'test' | null
  /** False until the flag resolves from either source. */
  isReady: boolean
}

const UNRESOLVED: AshleyFurnitureLaneState = {
  enabled: false,
  arm: null,
  isReady: false,
}

function statesEqual(
  left: AshleyFurnitureLaneState,
  right: AshleyFurnitureLaneState
): boolean {
  return (
    left.enabled === right.enabled &&
    left.arm === right.arm &&
    left.isReady === right.isReady
  )
}

export default function useAshleyFurnitureLane(): AshleyFurnitureLaneState {
  const posthog = usePostHog()

  const readState = (): AshleyFurnitureLaneState => {
    const sdkValue = posthog.getFeatureFlag(
      FeatureFlags.TogglerAshleyFurniture,
      { send_event: false }
    )
    const cookieValue = readBootstrappedFeatureFlag(
      FeatureFlags.TogglerAshleyFurniture
    )
    const value = sdkValue ?? cookieValue

    if (value === undefined) return UNRESOLVED

    // Only the literal `test` is treatment. A boolean `true` is deliberately
    // NOT treated as treatment: this is registered as a multivariate
    // experiment, and accepting `true` would silently enrol everyone if the
    // flag were ever recreated as a plain boolean.
    const arm = value === 'test' ? 'test' : 'control'
    return { enabled: arm === 'test', arm, isReady: true }
  }

  const [state, setState] = useState<AshleyFurnitureLaneState>(readState)

  useEffect(() => {
    const update = (): void => {
      setState((current) => {
        const next = readState()
        return statesEqual(current, next) ? current : next
      })
    }

    update()
    return posthog.onFeatureFlags(update)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog])

  return state
}

/**
 * The arm as it rides into `link_clicks.metadata.router_ashley_arm`.
 *
 * ⚠️ **`unresolved` is NOT `control`, and must never be folded into it.** The
 * broadcast can fire before the flag resolves (the bounded-timeout path in
 * `useAffiliateRouterBroadcastGate`), and such a user is routed to Wayfair while
 * having no assignment at all. Without this third value that click is
 * indistinguishable from a genuinely bucketed control click, which silently
 * pads the control arm with unassigned traffic and biases the eRPC comparison
 * toward whatever the fallback merchant earns. Same reasoning as the `arv`
 * `flag-timeout` sentinel this mirrors.
 *
 * This stamp — not the PostHog exposure event — is what makes the readout
 * trustworthy, because the primary metric (commission) lives in Postgres.
 */
export function ashleyFurnitureArmStamp(
  state: AshleyFurnitureLaneState
): AshleyFurnitureArmStamp {
  if (!state.isReady || state.arm === null) return 'unresolved'
  return state.arm
}

/**
 * One `toggler_ashley_furniture_exposure` per surface, mirroring
 * `useAffiliateRouterExposure`. Needed because every flag read passes
 * `send_event: false`, so PostHog sees no automatic exposure and the experiment
 * would otherwise have no denominator and no way to check for sample-ratio
 * mismatch.
 *
 * ⚠️ Denominator caveat, deliberate: this fires once for every router-`test`
 * user on an eligible surface, NOT once per furniture query. It is therefore
 * wider than the population Ashley can actually serve — plenty of these users
 * never type a furniture query at all. That is fine for SRM (assignment should
 * split 15/85 regardless of what anyone searches) but it is the WRONG
 * denominator for conversion. For anything per-click, use the `link_clicks`
 * rows carrying `router_ashley_arm` + `router_eligibility_match`, which are
 * emitted only on the furniture lane.
 */
export function useAshleyFurnitureLaneExposure({
  arm,
  eligible,
  surface,
}: {
  arm: 'control' | 'test' | null
  eligible: boolean
  surface: string
}): void {
  const posthog = usePostHog()
  const exposedRef = useRef(false)

  useEffect(() => {
    if (!arm || !eligible || exposedRef.current) return

    exposedRef.current = true
    posthog.capture('toggler_ashley_furniture_exposure', {
      variant: arm,
      surface,
    })
  }, [arm, eligible, posthog, surface])
}

/**
 * The `router_ashley_arm` query params for a routed click, or `{}` for none.
 *
 * Two independent reasons to emit nothing:
 *  - **Paused** (`ASHLEY_LANE_PAUSED`). Stamping while nothing routes to Ashley
 *    would write an arm onto rows where BOTH arms are Wayfair, so a later
 *    arm-vs-arm read over that period returns a null result that reads as
 *    "Ashley performs the same as Wayfair" rather than "Ashley wasn't served".
 *  - **No targeting rule on the lane.** Rides along only where
 *    `eligibilityMatch` is non-null (today `furniture_home`). Stamping every
 *    routed click would put an Ashley arm on fashion and beauty traffic the
 *    experiment cannot touch, inflating the denominator with rows that were
 *    never at risk.
 *
 * Extracted from `useBroadcastRedirectUrl` so the paused invariant is unit
 * testable — the hook itself has no spec.
 */
export function ashleyFurnitureArmQueryObject(
  hasEligibilityMatch: boolean,
  arm: AshleyFurnitureArmStamp,
  /**
   * Test seam only — defaults to the live flag. ⛔ Never pass this from
   * production code; a call site supplying `false` would silently re-enable the
   * stamp and defeat the pause.
   */
  paused: boolean = ASHLEY_LANE_PAUSED
): { router_ashley_arm?: AshleyFurnitureArmStamp } {
  if (paused || !hasEligibilityMatch) return {}
  return { router_ashley_arm: arm }
}
